/**
 * The app-level saved-views seam.
 *
 * A table cannot build its own saved-views backing: the source comes from the
 * app's `ApiClient`, the identity from the live session, and the permissions
 * from this app's scope catalogue. Declared here once,
 * `<AddressTable enableSavedViews>` and `<TransactionTable enableSavedViews>` are
 * booleans instead of six pages each assembling a source, an identity, a menu and
 * a save dialog — which is why the capability shipped unreachable.
 *
 * Session-scoped on purpose: the identity keys the stored views, so it belongs
 * below `<TenantProvider>` where a tenant switch re-keys it.
 */
import * as React from "react";
import { SavedViewsProvider } from "@authe/saved-views";
import { useSavedViewsSource } from "@/api/adapters";
import { useSavedViewPermissions } from "./savedViewPermissions";
import { useStorageIdentity } from "./storageIdentity";
import { DashboardSharePeople } from "./DashboardSharePeople";

export function DashboardSavedViews({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const source = useSavedViewsSource();
  const identity = useStorageIdentity();
  const perms = useSavedViewPermissions();
  // The third thing a package table cannot work out for itself. `@authe/tx-table`
  // has no access to this app's scope catalogue, so the two biggest tables were
  // left with the hook's own `canShare`/`canSave` defaults — which were `true`,
  // and offered "Shared (tenant)" (plus the promise that everyone in the tenant
  // would see the view) to callers the API refuses. The mapping is the one in
  // `useSavedViewPermissions`; nothing downstream re-derives it.
  const permissions = React.useMemo(
    () => ({
      canSave: perms.canPersonalize,
      // `create`, not this hook's `canShare` — that one is the tokenized
      // share-link verb (`edit`). The dialog's scope picker is about publishing
      // a view beyond yourself, and those are different grants.
      canShare: perms.canPublish,
      canDeleteShared: perms.canDeleteShared,
    }),
    [perms.canPersonalize, perms.canPublish, perms.canDeleteShared],
  );
  return (
    <SavedViewsProvider source={source} identity={identity} permissions={permissions}>
      {/* Who a share link can name, for every share dialog under this seam. */}
      <DashboardSharePeople>{children}</DashboardSharePeople>
    </SavedViewsProvider>
  );
}
